import { useState } from 'react'
import { Menu } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  useDeleteAccountNumberForOrganisation,
  useDeleteAccountNumberForTerminalOrganisation,
  usePostOrganisationOrganisationidAccountNumbers,
  usePostTerminalOrganisationidAccountNumbers,
  usePutOrganisationIdAccountNumbersAccountnumber,
  usePostSelfOnboardingResetLimits,
  usePostSelfOnboardingRemoveStatus,
  type BlockReasonDto,
} from '@dhl/mdp-api'
import { type GridColDef, Pagination } from '@dhl/components'
import { ConfirmationDialog } from '@dhl/confirmation-dialog'
import { BadgeInline, Body1, Box, Button, Card, Grid, IconButton, MenuItem, Tooltip } from '@dhl-ui/components'
import {
  DeleteIcon,
  MoreVerticalIcon,
  PlusIcon,
  EditSolidIcon,
  LockOpenIcon,
  UnavailableIcon,
  UserIcon,
} from '@dhl-ui/icons'
import { formatDayMonthYear } from '@dhl/utils'
import { postData } from '~/api/admin-api'
import { AdminDataGrid, insertIf, useNotificationMessage } from '~/common'
import {
  type Account,
  AccountForm,
  getInitialAccount,
  toAccount,
  toAccountNumberDto,
  useOrganisatonAccounts,
} from '~/common/components/accountnumbers'
import { type Product, useParcelTypes, useProducts, useShipmentOptions } from '~/common/model/capabilities'
import { getErrorMessage } from '~/common/util/axiosUtil'
import type { Organisation, OrganisationType } from '~/modules/organisations/model'

type Props = {
  organisation: Organisation
  businessType: OrganisationType
  withAccountStatus?: boolean
}

const isSelfOnboardingReason = (reason?: BlockReasonDto): boolean => {
  return String(reason) === 'SelfOnboardingLimitReached'
}

export const AccountDataTable = ({ organisation, businessType, withAccountStatus = false }: Props) => {
  const { t } = useTranslation()
  const [showAccountForm, setShowAccountForm] = useState<boolean>(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false)
  const [showUnblockConfirm, setShowUnblockConfirm] = useState<boolean>(false)
  const [showRemoveSelfOnboardedLabelConfirm, setShowRemoveSelfOnboardedLabelConfirm] = useState<boolean>(false)

  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)
  const [anchorEl, setAnchorEl] = useState(null)
  const menuOpen = Boolean(anchorEl)

  const { id, businessUnitCode: businessUnit } = organisation
  const fromCountry = organisation.data.address.countryCode
  const [selectedAccount, setSelectedAccount] = useState<Account | undefined>(undefined)
  const isNew = !selectedAccount

  const { products: allProducts } = useProducts(businessUnit, fromCountry)
  const { parcelTypes } = useParcelTypes(businessUnit, fromCountry)
  const { shipmentOptions } = useShipmentOptions(businessUnit)
  const { accountDtos, isFetching, refetch } = useOrganisatonAccounts(undefined, id, businessType)
  const organisationAccounts = accountDtos.map((acc) => toAccount(allProducts, parcelTypes, acc))
  const initialAccount = getInitialAccount(allProducts, fromCountry, id, businessUnit)

  // Organisation endpoints:
  const { mutateAsync: postOrganisationAccount } = usePostOrganisationOrganisationidAccountNumbers()
  const { mutateAsync: putOrganisationAccount } = usePutOrganisationIdAccountNumbersAccountnumber()
  const { mutateAsync: deleteOrganisationAccount } = useDeleteAccountNumberForOrganisation()

  // Terminal endpoints:
  const { mutateAsync: postTerminalAccount } = usePostTerminalOrganisationidAccountNumbers()
  const { mutateAsync: deleteTerminalAccount } = useDeleteAccountNumberForTerminalOrganisation()

  // Self Onboarding endpoints
  const { mutateAsync: postUnblockAccount } = usePostSelfOnboardingResetLimits()
  const { mutateAsync: postRemoveSelfOnboardedLabel } = usePostSelfOnboardingRemoveStatus()

  const { showError, showSuccess } = useNotificationMessage()

  const onAddButtonClick = () => {
    setSelectedAccount(undefined)
    setShowAccountForm(true)
  }

  const handleAccountFormClose = () => {
    setSelectedAccount(undefined)
    setShowAccountForm(false)
  }

  const handleDeleteConfirmClose = () => {
    setSelectedAccount(undefined)
    setShowDeleteConfirm(false)
    setAnchorEl(null)
  }

  const handleUnblockSelfOnboardingClose = () => {
    setSelectedAccount(undefined)
    setShowUnblockConfirm(false)
    setAnchorEl(null)
  }

  const handleRemoveSelfOnboardedLabelClose = () => {
    setSelectedAccount(undefined)
    setShowRemoveSelfOnboardedLabelConfirm(false)
    setAnchorEl(null)
  }

  const persistAccount = async (account: Account, allAccountProducts: Product[]) => {
    const accountDto = toAccountNumberDto(allAccountProducts, account)
    try {
      if (isNew) {
        const postEndpoint = businessType === 'organisation' ? postOrganisationAccount : postTerminalAccount
        await postEndpoint({ organisationId: id, data: accountDto })
      } else if (businessType === 'organisation') {
        await putOrganisationAccount({ id, accountNumber: account.id, data: accountDto })
      } else if (businessType === 'terminal') {
        // todo backend: Missing usePutTerminalIdAccountNumbersAccountnumber
        await postData(`${businessType}/${id}/account-numbers/${account.id}`, 'PUT', accountDto)
      }
      showSuccess(t(`admin.accountNumbers.${isNew ? 'add' : 'edit'}AccountNumberSuccess`))
      await refetch()
      handleAccountFormClose()
    } catch (error) {
      const message = getErrorMessage(error, t('admin.accountNumbers.errorMessage'))
      showError(message)
    }
  }

  const deleteAccount = async () => {
    try {
      const deleteEndpoint = businessType === 'organisation' ? deleteOrganisationAccount : deleteTerminalAccount
      await deleteEndpoint({ id, accountNumber: selectedAccount!.id })
      showSuccess(t('admin.accountNumbers.deleteAccountNumberSuccess'))
      handleDeleteConfirmClose()
      refetch()
    } catch (error) {
      const message = getErrorMessage(error, t('admin.accountNumbers.errorMessage'))
      showError(message)
    }
  }

  const unblockSelfOnboardingAccount = async () => {
    try {
      await postUnblockAccount({ data: { organisationId: id, accountNumber: selectedAccount!.id } })
      showSuccess(t('admin.accountNumbers.unblockAccountSuccess'))
      handleUnblockSelfOnboardingClose()
      refetch()
    } catch (error) {
      const message = getErrorMessage(error, t('admin.accountNumbers.errorMessage'))
      showError(message)
    }
  }

  const removeSelfOnboardedLabel = async () => {
    try {
      await postRemoveSelfOnboardedLabel({ data: { organisationId: id, accountNumber: selectedAccount!.id } })
      showSuccess(t('admin.accountNumbers.removeSelfOnboardedLabelSuccess'))
      handleRemoveSelfOnboardedLabelClose()
      refetch()
    } catch (error) {
      const message = getErrorMessage(error, t('admin.accountNumbers.errorMessage'))
      showError(message)
    }
  }

  const handleRowClick = (row: Account) => {
    setSelectedAccount(row)
    setShowAccountForm(true)
  }

  const handleEditClick = () => {
    setShowAccountForm(true)
    setAnchorEl(null)
  }

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true)
    setAnchorEl(null)
  }

  const handleUnblockClick = () => {
    setShowUnblockConfirm(true)
    setAnchorEl(null)
  }

  const handleRemoveSelfOnboardedLabelClick = () => {
    setShowRemoveSelfOnboardedLabelConfirm(true)
    setAnchorEl(null)
  }

  const renderSelectedProducts = (row: Account) => {
    const label = row.selectedProducts.map((p) => p.value).join(', ')
    return (
      <Box title={label} sx={{ whiteSpace: 'normal', lineHeight: 1 }}>
        {label}
      </Box>
    )
  }

  const renderAccountNumber = (account: Account) => {
    const {
      blockedInfo: { isBlocked, since: blockedSince, reason: blockedReason },
      selfOnboardingInfo,
    } = account
    const isSelfOnboardingLimitReached = isBlocked && isSelfOnboardingReason(blockedReason)
    const isCreditStop = isBlocked && blockedReason?.type === 'CreditStop'
    const bullet = isSelfOnboardingLimitReached ? (
      <UnavailableIcon color="warning" sx={{ mr: 1, fontSize: '0.7rem' }} data-testid="self-onboarding-limit-reached" />
    ) : (
      <BadgeInline color={isCreditStop || isBlocked ? 'error' : 'success'} sx={{ mr: 1, width: '0.7rem' }} />
    )
    return (
      <Grid container direction="row" gap={0.5} alignItems="center" wrap="nowrap">
        {withAccountStatus && (
          <Grid>
            {isBlocked ? (
              <Tooltip
                title={t(!!blockedSince ? 'admin.since' : 'admin.dateUnknown', {
                  date: formatDayMonthYear(blockedSince),
                })}
              >
                <div>{bullet}</div>
              </Tooltip>
            ) : (
              <>{bullet}</>
            )}
          </Grid>
        )}
        <Grid>
          {selfOnboardingInfo && selfOnboardingInfo.since ? (
            <Tooltip
              title={t('admin.accountNumbers.selfOnboardedTooltip', {
                date: formatDayMonthYear(selfOnboardingInfo.since),
                parcels: selfOnboardingInfo.sentParcelsLastWeek,
                pallets: selfOnboardingInfo.sentPalletsLastWeek,
              })}
            >
              <Grid container direction="column" alignItems="flex-start">
                <Grid sx={{ fontSize: '0.75rem', lineHeight: 1.2, color: 'grey.600' }}>Self-onboarded</Grid>
                <Grid>{account.id}</Grid>
              </Grid>
            </Tooltip>
          ) : (
            account.id
          )}
        </Grid>
      </Grid>
    )
  }

  const renderActionsCell = (account: Account) => {
    return (
      <div key={`id_${account.id}`}>
        <IconButton
          aria-controls={menuOpen ? 'more-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={menuOpen ? 'true' : undefined}
          aria-label="more"
          id="more-button"
          onClick={(event: any) => {
            setAnchorEl(event.currentTarget)
            setSelectedAccount(account)
          }}
        >
          <MoreVerticalIcon />
        </IconButton>
      </div>
    )
  }

  const columns: GridColDef<Account>[] = [
    {
      field: 'id',
      headerName: t('admin.accountNumbers.accountNumber'),
      flex: 2,
      renderCell: ({ row }) => renderAccountNumber(row),
      sortable: true,
    },
    {
      field: 'accountType',
      headerName: t('admin.accountNumbers.accountType'),
      flex: 1,
      sortable: true,
    },
    {
      field: 'selectedProducts',
      headerName: t('admin.accountNumbers.products'),
      flex: 4,
      renderCell: ({ row }) => renderSelectedProducts(row),
    },
    {
      field: 'actions',
      headerName: t('admin.accountNumbers.actions'),
      flex: 1,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ row }) => renderActionsCell(row),
    },
  ]

  const getMenuItems = (account?: Account) => {
    const isSelfOnboardingLimitReached =
      account?.blockedInfo.isBlocked && isSelfOnboardingReason(account?.blockedInfo.reason)

    return [
      {
        key: 'edit',
        onClick: handleEditClick,
        icon: <EditSolidIcon data-testid="edit-account-details" />,
        label: t('admin.accountNumbers.edit'),
        ariaLabel: 'edit account details',
      },
      {
        key: 'delete',
        onClick: handleDeleteClick,
        icon: <DeleteIcon data-testid="delete-account-number" />,
        label: t('admin.accountNumbers.delete'),
        ariaLabel: 'delete account number',
      },
      ...insertIf(!!isSelfOnboardingLimitReached, {
        key: 'unblock',
        onClick: handleUnblockClick,
        icon: <LockOpenIcon data-testid="extra-action-1" />,
        label: t('admin.accountNumbers.unblock'),
        ariaLabel: 'unblock account',
      }),
      ...insertIf(!!account?.selfOnboardingInfo, {
        key: 'remove-self-onboarded-label',
        onClick: handleRemoveSelfOnboardedLabelClick,
        icon: <UserIcon data-testid="extra-action-2" />,
        label: t('admin.accountNumbers.removeSelfOnboardedLabel'),
        ariaLabel: 'remove self-onboarded label',
      }),
    ]
  }

  return (
    <>
      <Card>
        <Card.Header
          title={t('admin.usersPage.organisationDetailsView.accountNumber')}
          endNode={
            <Button
              size="small"
              data-testid="add-accountNumber"
              color="primary"
              variant="contained"
              startIcon={<PlusIcon />}
              onClick={() => onAddButtonClick()}
            >
              {t('admin.accountNumbers.addAccountNumber')}
            </Button>
          }
        />
        <Card.Content>
          <AdminDataGrid
            loading={isFetching}
            data-testid="account-table"
            autoHeight
            rows={organisationAccounts}
            columns={columns}
            paginationModel={{
              page,
              pageSize,
            }}
            onRowClick={({ row }) => handleRowClick(row)}
            rowHeight={60}
            columnHeaderHeight={48}
            disableRowSelectionOnClick
            disableColumnFilter
            hideFooter
          />

          <Box px={2} py={1}>
            <Pagination
              page={page}
              count={organisationAccounts.length}
              onPageChange={(_, page) => setPage(page)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={setPageSize}
            />
          </Box>
        </Card.Content>
      </Card>

      <Menu
        id="more-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{
          'aria-labelledby': 'more-button',
        }}
      >
        {getMenuItems(selectedAccount).map((item) => (
          <MenuItem key={item.key} onClick={item.onClick} icon={item.icon} isDestructive={item.key === 'delete'}>
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      {showAccountForm && (
        <AccountForm
          isNew={isNew}
          account={selectedAccount ?? initialAccount}
          businessType={businessType}
          businessUnit={businessUnit}
          shipmentOptions={shipmentOptions}
          onSubmit={persistAccount}
          onCancel={handleAccountFormClose}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmationDialog
          open={true}
          titleText={t('admin.accountNumbers.dangerZone')}
          acceptText={t('admin.accountNumbers.deleteAccountNumber')}
          rejectText={t('admin.accountNumbers.cancel')}
          onAccept={deleteAccount}
          onReject={handleDeleteConfirmClose}
        >
          <Body1>{t('admin.accountNumbers.deleteMessage', { accountNr: selectedAccount?.id })}</Body1>
        </ConfirmationDialog>
      )}

      {showUnblockConfirm && (
        <ConfirmationDialog
          open={true}
          titleText={t('admin.accountNumbers.unblockSelfOnboardingAccount')}
          acceptText={t('admin.accountNumbers.unblock')}
          rejectText={t('admin.accountNumbers.cancel')}
          onAccept={unblockSelfOnboardingAccount}
          onReject={() => setShowUnblockConfirm(false)}
        >
          <Body1>{t('admin.accountNumbers.unblockMessage', { accountNr: selectedAccount?.id })}</Body1>
        </ConfirmationDialog>
      )}

      {showRemoveSelfOnboardedLabelConfirm && (
        <ConfirmationDialog
          open={true}
          titleText={t('admin.accountNumbers.removeSelfOnboardedLabel')}
          acceptText={t('admin.accountNumbers.removeSelfOnboardedLabelShort')}
          rejectText={t('admin.accountNumbers.cancel')}
          onAccept={removeSelfOnboardedLabel}
          onReject={() => setShowRemoveSelfOnboardedLabelConfirm(false)}
        >
          <Body1>{t('admin.accountNumbers.removeSelfOnboardedLabelMessage', { accountNr: selectedAccount?.id })}</Body1>
        </ConfirmationDialog>
      )}
    </>
  )
}
